import React, { FC } from 'react';
import {
  CapabilityDTO,
  CapabilityDTOFromJSON,
  SearchProductDTO,
  SearchProductDTOFromJSON,
  SearchResultDTOTypeEnum,
  SearchTeamDTO,
  SearchTeamDTOFromJSON,
  SloDTO,
  SloDTOFromJSON,
} from '@atlassiansox/phobos-client-ts';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';

import DraftStatusLozenge from '@/components/composed/slo-card/lozenges/draft-status-lozenge';
import { BreachCount } from '@/components/primitive';
import { determineSloStatus } from '@/components/composed/slo-card/utils';
import { SLOStatus } from '@/components/composed/slo-card/types';
import { flexStart, spaceGapCompact } from '@/styles';

import { SearchResultItemType } from '../types';
import { testIds } from '../../constants';

import * as styles from './styles';
import SearchKeyValueTag from './search-key-value-tag';

type SearchListStatusProps = {
  item: SearchResultItemType;
  type: SearchResultDTOTypeEnum;
};

const SearchListStatus: FC<SearchListStatusProps> = ({ item, type }) => {
  let displayElement = <></>;

  switch (type) {
    case SearchResultDTOTypeEnum.Slo: {
      const slo: SloDTO = SloDTOFromJSON(item);
      const { isDraft, performance, objective } = slo;
      const sloStatus: SLOStatus = determineSloStatus({ performance, objective });

      displayElement = (
        <div css={[styles.searchResultTagColumnStyles, styles.searchResultColumnStyles]}>
          {isDraft && <DraftStatusLozenge isCompact />}
          {sloStatus === 'breaches' && <Lozenge appearance="removed">breaching</Lozenge>}
          {sloStatus === 'neither' && <Lozenge>n/a</Lozenge>}
        </div>
      );
      break;
    }
    case SearchResultDTOTypeEnum.Capability: {
      const capability: CapabilityDTO = CapabilityDTOFromJSON(item);
      const { breaches, sloCount, qualityIssues } = capability;
      const hasSloCount: boolean = sloCount !== undefined && sloCount >= 0;

      displayElement = (
        <div data-testid={testIds.searchResultListTitleCount} css={[flexStart, spaceGapCompact]}>
          <SearchKeyValueTag title="breaching slos" isBadge>
            <BreachCount count={breaches} />
          </SearchKeyValueTag>
          <SearchKeyValueTag title="with quality issue slos" isBadge>
            <BreachCount isWarning count={qualityIssues} />
          </SearchKeyValueTag>
          {hasSloCount && (
            <SearchKeyValueTag title="total" isBadge>
              <Badge max={Infinity} appearance="default">
                {sloCount}
              </Badge>
            </SearchKeyValueTag>
          )}
        </div>
      );
      break;
    }
    case SearchResultDTOTypeEnum.Team: {
      const team: SearchTeamDTO = SearchTeamDTOFromJSON(item);
      const { totalCapabilities } = team;

      displayElement = (
        <div data-testid={testIds.searchResultListTitleCount}>
          <SearchKeyValueTag title="total capabilities" isBadge>
            <Badge appearance={totalCapabilities > 0 ? 'primary' : undefined}>
              {totalCapabilities}
            </Badge>
          </SearchKeyValueTag>
        </div>
      );
      break;
    }
    case SearchResultDTOTypeEnum.Product: {
      const product: SearchProductDTO = SearchProductDTOFromJSON(item);
      const { totalCapabilities } = product;

      displayElement = (
        <div data-testid={testIds.searchResultListTitleCount}>
          <SearchKeyValueTag title="total capabilities" isBadge>
            <Badge appearance={totalCapabilities > 0 ? 'primary' : undefined}>
              {totalCapabilities}
            </Badge>
          </SearchKeyValueTag>
        </div>
      );
      break;
    }
  }

  return <div css={styles.searchResultColumnStyles}>{displayElement}</div>;
};

export default SearchListStatus;
